/**
 * KYC Constants and Enums
 * Aligned with backend/shared/database/kyc_models.py
 * Swiss Banking compliance constants
 */

/**
 * KYC Profile Status States
 * Matches database enum
 */
export enum KYCStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
  SUSPENDED = 'suspended',
  CHANGES_REQUESTED = 'changes_requested',  // TASK-372: CO requests changes
}

/**
 * Risk Classification Levels
 * Matches database enum
 */
export enum RiskLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

/**
 * Politically Exposed Person Classification
 * Matches database enum
 */
export enum PEPClassification {
  NOT_PEP = 'not_pep',
  PEP = 'pep',
  RCA = 'rca', // Relative or Close Associate
  FORMER_PEP = 'former_pep'
}

/**
 * Swiss-specific regulatory compliance requirements
 */
export const SwissRegulations = {
  FINSA: 'FinSA', // Financial Services Act
  FINIA: 'FinIA', // Financial Institutions Act
  AMLA: 'AMLA', // Anti-Money Laundering Act
  DPA: 'DPA', // Data Protection Act
  CO: 'CO' // Code of Obligations
} as const;

/**
 * Client Classification under Swiss FinSA
 */
export const ClientClassification = {
  PRIVATE_CLIENT: 'private_client', // Retail, non-qualified
  PROFESSIONAL_CLIENT: 'professional_client', // Qualified investor
  INSTITUTIONAL_CLIENT: 'institutional_client' // Banks, insurance, etc.
} as const;

/**
 * Swiss transaction thresholds for compliance triggers
 */
export const TransactionThresholds = {
  SMALL: 100000, // < 100k CHF
  MEDIUM: 500000, // 100k-500k CHF
  LARGE: 2000000, // 500k-2M CHF
  VERY_LARGE: 10000000 // > 10M CHF
} as const;

/**
 * KYC document types
 */
export const DocumentTypes = {
  PASSPORT: 'passport',
  ID_CARD: 'id_card',
  DRIVERS_LICENSE: 'drivers_license',
  PROOF_OF_ADDRESS: 'proof_of_address',
  BANK_STATEMENT: 'bank_statement',
  TAX_RETURN: 'tax_return',
  EMPLOYMENT_LETTER: 'employment_letter',
  UTILITY_BILL: 'utility_bill',
  COMPANY_REGISTRATION: 'company_registration',
  OTHER: 'other'
} as const;

/**
 * Income source types
 */
export const IncomeTypes = {
  SALARY: 'salary',
  BONUS: 'bonus',
  DIVIDENDS: 'dividends',
  RENTAL: 'rental',
  PENSION: 'pension',
  SOCIAL_SECURITY: 'social_security',
  INVESTMENT: 'investment',
  BUSINESS: 'business',
  OTHER: 'other'
} as const;

/**
 * Wealth source types
 */
export const WealthSourceTypes = {
  EMPLOYMENT: 'employment',
  BUSINESS: 'business',
  INHERITANCE: 'inheritance',
  INVESTMENT: 'investment',
  REAL_ESTATE: 'real_estate',
  GIFT: 'gift',
  LOTTERY: 'lottery',
  OTHER: 'other'
} as const;

/**
 * Asset types for wealth composition
 */
export const AssetTypes = {
  CASH: 'cash',
  SECURITIES: 'securities',
  REAL_ESTATE: 'real_estate',
  BUSINESS_EQUITY: 'business_equity',
  COMMODITIES: 'commodities',
  CRYPTO: 'crypto',
  ART_COLLECTIBLES: 'art_collectibles',
  OTHER: 'other'
} as const;

/**
 * Swiss-specific validation patterns
 */
export const SwissPatterns = {
  // Swiss phone: +41 XX XXX XX XX
  PHONE: /^\+41\s?(?:\d{2}\s?\d{3}\s?\d{2}\s?\d{2}|\d{9})$/,

  // Swiss postal code: 4 digits
  POSTAL_CODE: /^[1-9]\d{3}$/,

  // Swiss AHV/AVS number: 756.XXXX.XXXX.XX
  AHV_NUMBER: /^756\.\d{4}\.\d{4}\.\d{2}$/,

  // Swiss IBAN: CH + 2 check digits + 5 bank code + 12 account
  IBAN: /^CH\d{2}\s?(?:\d{4}\s?){4}\d$/,

  // Swiss company registration: CHE-XXX.XXX.XXX
  CHE_NUMBER: /^CHE-\d{3}\.\d{3}\.\d{3}$/,

  // Swiss VAT number: CHE-XXX.XXX.XXX MWST/TVA/IVA
  VAT_NUMBER: /^CHE-\d{3}\.\d{3}\.\d{3}\s?(MWST|TVA|IVA)$/
} as const;

/**
 * Risk factor categories
 */
export const RiskCategories = {
  GEOGRAPHIC: 'geographic',
  PRODUCT: 'product',
  CLIENT: 'client',
  TRANSACTION: 'transaction',
  REGULATORY: 'regulatory'
} as const;

/**
 * Compliance thresholds and limits
 */
export const ComplianceThresholds = {
  KYC_EXPIRY_WARNING_DAYS: 30,
  DOCUMENT_EXPIRY_WARNING_DAYS: 60,
  HIGH_VALUE_TRANSACTION_LIMIT: 1000000, // 1M CHF
  COMPLEX_PRODUCTS_REQUIRE_DISCLOSURE: true,
  US_PERSON_REQUIRES_FATCA: true,
  PROFESSIONAL_CLIENT_MIN_PORTFOLIO: 500000, // 500k CHF

  // KYC validity periods by risk level (in days)
  KYC_VALIDITY: {
    LOW: 1095, // 3 years
    MEDIUM: 730, // 2 years
    HIGH: 365, // 1 year
    CRITICAL: 180 // 6 months
  }
} as const;

/**
 * Swiss cantons for address validation
 */
export const SwissCantons = [
  'AG', 'AI', 'AR', 'BE', 'BL', 'BS', 'FR', 'GE', 'GL', 'GR',
  'JU', 'LU', 'NE', 'NW', 'OW', 'SG', 'SH', 'SO', 'SZ', 'TG',
  'TI', 'UR', 'VD', 'VS', 'ZG', 'ZH'
] as const;

/**
 * Supported languages for Swiss market
 */
export const SupportedLanguages = {
  EN: 'en', // English
  DE: 'de', // German
  FR: 'fr', // French
  IT: 'it' // Italian
} as const;

/**
 * Employment status options
 */
export const EmploymentStatus = {
  EMPLOYED: 'employed',
  SELF_EMPLOYED: 'selfEmployed',
  UNEMPLOYED: 'unemployed',
  RETIRED: 'retired',
  STUDENT: 'student',
  OTHER: 'other'
} as const;

/**
 * Business types
 */
export const BusinessTypes = {
  SOLE_PROPRIETORSHIP: 'sole_proprietorship',
  PARTNERSHIP: 'partnership',
  CORPORATION: 'corporation',
  LLC: 'llc',
  TRUST: 'trust',
  FOUNDATION: 'foundation',
  OTHER: 'other'
} as const;

/**
 * Education levels (Story 2.2.6)
 */
export const EducationLevels = {
  HIGH_SCHOOL: 'high_school',
  BACHELOR: 'bachelor',
  MASTER: 'master',
  PHD: 'phd',
  PROFESSIONAL_DEGREE: 'professional_degree',
  OTHER: 'other'
} as const;

/**
 * Common hobbies and interests for wealth management clients (Story 2.2.6)
 */
export const CommonHobbies = [
  'Sailing',
  'Golf',
  'Tennis',
  'Skiing',
  'Art Collecting',
  'Wine Tasting',
  'Fine Dining',
  'Travel',
  'Yachting',
  'Equestrian',
  'Classic Cars',
  'Photography',
  'Music',
  'Theater',
  'Opera',
  'Reading',
  'Philanthropy',
  'Hiking',
  'Cycling',
  'Cooking'
] as const;

/**
 * Spouse/Partner employment status options (Story 2.2.6)
 */
export const SpouseEmploymentStatus = {
  EMPLOYED: 'employed',
  SELF_EMPLOYED: 'selfEmployed',
  UNEMPLOYED: 'unemployed',
  RETIRED: 'retired',
  HOMEMAKER: 'homemaker',
  OTHER: 'other'
} as const;

/**
 * High-risk countries list (sample - should be maintained externally)
 */
export const HighRiskCountries = [
  'AF', 'AL', 'BS', 'BW', 'KH', 'GH', 'IS', 'MN', 'MM', 'NI',
  'PK', 'PA', 'SY', 'UG', 'YE', 'ZW', 'IR', 'KP'
] as const;

/**
 * Type exports for constants
 */
export type SwissRegulation = typeof SwissRegulations[keyof typeof SwissRegulations];
export type ClientClassificationType = typeof ClientClassification[keyof typeof ClientClassification];
export type DocumentType = typeof DocumentTypes[keyof typeof DocumentTypes];
export type IncomeType = typeof IncomeTypes[keyof typeof IncomeTypes];
export type WealthSourceType = typeof WealthSourceTypes[keyof typeof WealthSourceTypes];
export type AssetType = typeof AssetTypes[keyof typeof AssetTypes];
export type RiskCategory = typeof RiskCategories[keyof typeof RiskCategories];
export type SwissCanton = typeof SwissCantons[number];
export type SupportedLanguage = typeof SupportedLanguages[keyof typeof SupportedLanguages];
export type EmploymentStatusType = typeof EmploymentStatus[keyof typeof EmploymentStatus];
export type BusinessType = typeof BusinessTypes[keyof typeof BusinessTypes];
export type HighRiskCountry = typeof HighRiskCountries[number];
export type EducationLevel = typeof EducationLevels[keyof typeof EducationLevels];
export type CommonHobby = typeof CommonHobbies[number];
export type SpouseEmploymentStatusType = typeof SpouseEmploymentStatus[keyof typeof SpouseEmploymentStatus];

/**
 * KYC Section Icon Map — shared across KYCReviewLayout and KYCSidebar (H21)
 * Maps icon name strings from dynamic section configs to Lucide icon components.
 */
import {
  User,
  Phone,
  Briefcase,
  GraduationCap,
  Receipt,
  Shield,
  DollarSign,
  TrendingUp,
  FileText,
  Building,
  Flag,
  type LucideIcon,
} from 'lucide-react';

export const KYC_SECTION_ICON_MAP: Record<string, LucideIcon> = {
  User,
  Phone,
  Briefcase,
  GraduationCap,
  Receipt,
  Shield,
  DollarSign,
  TrendingUp,
  FileText,
  Building,
  Flag,
};